import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, Button } from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PaletteIcon from '@mui/icons-material/Palette';

function HomePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const productId = searchParams.get('p');
    if (productId) {
      navigate(`/try?p=${productId}`);
    }

    // Clean up legacy provider flag if present
    localStorage.removeItem('tryOnProvider');
  }, [searchParams, navigate]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(160deg, #1a1a2e 0%, #16213e 40%, #0f3460 100%)',
        px: 3,
        pt: 'calc(32px + env(safe-area-inset-top))',
        pb: 'calc(32px + env(safe-area-inset-bottom))',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Decorative blurred circles */}
      <Box sx={{ position: 'absolute', top: '-15%', right: '-20%', width: '300px', height: '300px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(102,126,234,0.15) 0%, transparent 70%)', filter: 'blur(40px)' }} />
      <Box sx={{ position: 'absolute', bottom: '-10%', left: '-15%', width: '250px', height: '250px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(118,75,162,0.15) 0%, transparent 70%)', filter: 'blur(40px)' }} />

      {/* App icon */}
      <Box
        component="img"
        src="/icon.svg"
        alt="ProvaMi"
        sx={{
          width: { xs: '96px', sm: '120px' },
          height: { xs: '96px', sm: '120px' },
          mb: 2,
          borderRadius: '24px',
          boxShadow: '0 12px 32px rgba(102,126,234,0.3)',
          zIndex: 1
        }}
      />

      {/* Logo */}
      <Typography
        component="h1"
        sx={{
          fontSize: { xs: '42px', sm: '56px' },
          fontWeight: 900,
          letterSpacing: { xs: '5px', sm: '7px' },
          background: 'linear-gradient(135deg, #ffffff 0%, rgba(255,255,255,0.7) 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          lineHeight: 1,
          textAlign: 'center',
          mb: 1.5,
          position: 'relative',
          zIndex: 1
        }}
      >
        PROVAMI
      </Typography>

      {/* Tagline — with highlighted key actions */}
      <Typography
        sx={{
          fontSize: { xs: '17px', sm: '20px' },
          textAlign: 'center',
          color: 'rgba(255, 255, 255, 0.92)',
          maxWidth: '360px',
          lineHeight: 1.5,
          mb: { xs: 4, sm: 5 },
          fontWeight: 500,
          zIndex: 1,
          px: 2
        }}
      >
        <Box component="span" sx={{ color: '#fff', fontWeight: 700 }}>Prova virtualmente</Box> i capi{' '}
        <Box component="span" sx={{ color: '#fff', fontWeight: 700 }}>e scopri</Box> i{' '}
        <Box
          component="span"
          sx={{
            fontWeight: 700,
            background: 'linear-gradient(135deg, #9ba8ff, #c8a7ff)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}
        >
          colori che ti valorizzano
        </Box>
      </Typography>

      {/* CTA buttons wrapper — perfect vertical alignment */}
      <Box
        sx={{
          width: '100%',
          maxWidth: '340px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: 1.5,
          zIndex: 1,
          // Override MUI's default adjacent-sibling margin on Buttons
          '& > .MuiButton-root': { marginLeft: '0 !important' }
        }}
      >
        {/* CTA Scannerizza */}
        <Button
          fullWidth
          variant="contained"
          startIcon={<QrCodeScannerIcon sx={{ fontSize: '24px !important' }} />}
          onClick={() => navigate('/scan')}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            fontSize: { xs: '16px', sm: '18px' },
            fontWeight: 700,
            textTransform: 'none',
            py: { xs: 1.75, sm: 2 },
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(102, 126, 234, 0.35)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            '&:hover': {
              boxShadow: '0 12px 40px rgba(102, 126, 234, 0.5)',
              transform: 'translateY(-2px)',
              transition: 'all 0.3s ease'
            }
          }}
        >
          Scannerizza
        </Button>

        {/* CTA Catalogo */}
        <Button
          fullWidth
          variant="outlined"
          startIcon={<StorefrontIcon sx={{ fontSize: '24px !important' }} />}
          onClick={() => navigate('/catalogo')}
          sx={{
            background: 'rgba(255, 255, 255, 0.05)',
            color: 'rgba(255,255,255,0.85)',
            fontSize: { xs: '15px', sm: '16px' },
            fontWeight: 600,
            textTransform: 'none',
            py: { xs: 1.5, sm: 1.75 },
            borderRadius: '16px',
            border: '1.5px solid rgba(255, 255, 255, 0.15)',
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1.5px solid rgba(255, 255, 255, 0.3)',
              transform: 'translateY(-1px)',
              transition: 'all 0.3s ease'
            }
          }}
        >
          Catalogo
        </Button>

        {/* CTA Armocromia */}
        <Button
          fullWidth
          startIcon={<PaletteIcon sx={{ fontSize: '20px !important' }} />}
          onClick={() => navigate('/photo-capture?flow=armocromia')}
          sx={{
            mt: 0.5,
            color: 'rgba(255, 255, 255, 0.55)',
            fontSize: '14px',
            fontWeight: 500,
            textTransform: 'none',
            py: 1,
            '&:hover': {
              color: 'rgba(255,255,255,0.85)',
              backgroundColor: 'transparent'
            }
          }}
        >
          Armocromia
        </Button>
      </Box>

      {/* Steps — minimal */}
      <Box
        sx={{
          display: 'flex',
          gap: 4,
          mt: { xs: 5, sm: 6 },
          zIndex: 1
        }}
      >
        {[
          { num: '1', text: 'Scannerizza' },
          { num: '2', text: 'Scatta foto' },
          { num: '3', text: 'Prova il capo' }
        ].map((step) => (
          <Box key={step.num} sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontSize: '18px', fontWeight: 800, color: 'rgba(255,255,255,0.2)', mb: 0.3 }}>
              {step.num}
            </Typography>
            <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)', fontWeight: 500 }}>
              {step.text}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default HomePage;
